import React, { forwardRef, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { submitOrder } from '@ff-os/pdp-shared-redux/apis/submitOrder';
import { IDT } from '@ff-os/pdp-shared-redux/IDT';
import { selectHasPushError } from '@ff-os/pdp-shared-redux/slices/snapshotSlice';
import { OrderButtonWithPriceLabel } from '@ff-os/shared-react/components/order-button/OrderButtonWithPriceLabel';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { selectIsPremiumAvailable, selectPrintQuality } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { selectAllPhotos, hasValidAccessoryQuantities, selectFirstGroupWithAccessoryError } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { actions as qualityActions } from '@ff-scene-plugins/photo-prints-redux/slices/qualitySlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { selectTotalRequest } from '../_redux-groups';
import { getPhotosStatus, selectAmountOfUploadsRemaining } from '../_redux-photos';
import { AddingToCartWindow } from './AddingToCartWindow';
import { PhotoPrintsUploadProgress } from './PhotoPrintsUploadProgress';
import { usePremiumSurchargeLabel } from './premium/usePremiumSurchargeLabel';

const ChoosePrintQualityWindow = lazy(() => import('./premium/ChoosePrintQualityWindow')
    .then(m => ({ default: m.ChoosePrintQualityWindow })));

const NameGroupsWindow = lazy(() => import('./premium/NameGroupsWindow')
    .then(m => ({ default: m.NameGroupsWindow })));

const _PhotoPrintsOrderButton = forwardRef<HTMLDivElement, Props>(({
    disabled, placeOrder, isUploading,
    className, loading, priceRequest,
    amountOfUploadsRemaining, hasPushError,
    hasValidAccessoryQuantities, firstGroupWithAccessoryError,
    isPremiumAvailable, printQuality, setPrintQuality
}, ref) => {

    const { t } = useTranslation();

    const surchargeLabel = usePremiumSurchargeLabel();

    const [isOpen, setOpen] = useState(false);
    const [hasError, setError] = useState(false);
    const [wizardStep, setWizardStep] = useState<'quality' | 'names' | null>(null);
    const isMobile = useMobile();

    const placeOrderRef = useRef<Promise<void> | null>(null);

    const tryPlaceOrder = useCallback(() => {
        if (placeOrderRef.current === null) {
            setError(false);
            const promise = placeOrder();
            placeOrderRef.current = promise;
            promise
                .catch(() => setError(true))
                .then(() => placeOrderRef.current = null);
        }
    }, [placeOrder]);

    const scrollToFirstErrorGroup = useCallback(() => {
        if (firstGroupWithAccessoryError) {
            const element = document.querySelector(`[data-group-id="${firstGroupWithAccessoryError}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Trigger pulse animation after scroll completes
                setTimeout(() => {
                    element.classList.add('pulse-attention-trigger');
                    // Remove the class after animation completes
                    setTimeout(() => {
                        element.classList.remove('pulse-attention-trigger');
                    }, 400); // Match animation duration
                }, 100); // Short delay since scroll is instant
            }
        }
    }, [firstGroupWithAccessoryError]);

    const proceedToOrder = useCallback(() => {
        setOpen(true);
        if (!isUploading) {
            tryPlaceOrder();
        }
    }, [isUploading, tryPlaceOrder]);

    const onClick = useCallback(() => {
        if (!disabled) {
            if (!hasValidAccessoryQuantities) {
                scrollToFirstErrorGroup();
                return;
            }

            if (isPremiumAvailable) {
                setWizardStep('quality');
                return;
            }

            proceedToOrder();
        }
    }, [disabled, hasValidAccessoryQuantities, scrollToFirstErrorGroup, isPremiumAvailable, proceedToOrder]);

    const commitQuality = useCallback((quality: PhotoPrintsJson.PrintQuality) => {
        setPrintQuality(quality);
        setWizardStep(null);
        proceedToOrder();
    }, [setPrintQuality, proceedToOrder]);

    const chooseStandard = useCallback(() => commitQuality('standard'), [commitQuality]);

    const choosePremium = useCallback(() => {
        setWizardStep('names');
    }, []);

    const confirmNames = useCallback(() => commitQuality('premium'), [commitQuality]);

    const closeWizard = useCallback(() => {
        setWizardStep(null);
    }, []);

    useEffect(() => {
        if (isOpen && amountOfUploadsRemaining === 0) {
            tryPlaceOrder();
        }
    }, [amountOfUploadsRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (hasPushError) {
            setError(true);
        }
    }, [hasPushError]);

    return (
        <>
            <div ref={ref}>
                <OrderButtonWithPriceLabel
                    className={className} fullWidth={isMobile}
                    disabled={disabled} loading={loading}
                    onClick={onClick} priceRequest={priceRequest}
                    pluginName='photo-prints'>

                    {t('photo_prints.order_button.text')}

                </OrderButtonWithPriceLabel>
            </div>

            <WindowStackItem>
                <>
                    <Suspense fallback={null}>
                        {wizardStep === 'quality' && (
                            <ChoosePrintQualityWindow
                                close={closeWizard}
                                surchargeLabel={surchargeLabel}
                                initialQuality={printQuality}
                                onChooseStandard={chooseStandard}
                                onChoosePremium={choosePremium} />
                        )}
                        {wizardStep === 'names' && (
                            <NameGroupsWindow
                                back={() => setWizardStep('quality')}
                                close={closeWizard}
                                onConfirm={confirmNames} />
                        )}
                    </Suspense>
                    {isOpen ? (
                        <AddingToCartWindow
                            hasError={hasError}
                            dismissError={() => setError(false)}
                            close={() => setOpen(false)}
                            amountOfUploadsRemaining={amountOfUploadsRemaining}
                            isAddingToCart={placeOrderRef.current !== null} />
                    ) : (
                        <PhotoPrintsUploadProgress />
                    )}
                </>
            </WindowStackItem>
        </>
    );
});

type Props = {
    disabled: boolean;
    placeOrder: () => Promise<void>;
    priceRequest: IDT.TotalPriceRequest;
    className?: string;
    loading: boolean;
    isUploading: boolean;
    hasPushError: boolean;
    amountOfUploadsRemaining: number;
    hasValidAccessoryQuantities: boolean;
    firstGroupWithAccessoryError: string | null;
    isPremiumAvailable: boolean;
    printQuality: PhotoPrintsJson.PrintQuality;
    setPrintQuality: (printQuality: PhotoPrintsJson.PrintQuality) => void;
};

const mapStateToProps = (state: PhotoPrintsPdpState) => ({
    disabled: selectDisabled(state),
    priceRequest: selectTotalRequest(state),
    isUploading: selectIsUploading(state),
    loading: selectIsPending(state),
    hasPushError: selectHasPushError(state),
    amountOfUploadsRemaining: selectAmountOfUploadsRemaining(state),
    hasValidAccessoryQuantities: hasValidAccessoryQuantities(state),
    firstGroupWithAccessoryError: selectFirstGroupWithAccessoryError(state),
    isPremiumAvailable: selectIsPremiumAvailable(state),
    printQuality: selectPrintQuality(state)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: { quantity?: number }) => ({
    placeOrder: () => dispatch(submitOrder(ownProps.quantity)),
    setPrintQuality: (printQuality: PhotoPrintsJson.PrintQuality) => dispatch(qualityActions.overwrite(printQuality))
});

export const PhotoPrintsOrderButton = connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { forwardRef: true }
)(_PhotoPrintsOrderButton);

const selectIsUploading = createSelector([
    getPhotosStatus
], status => status === 'pending');

const selectHasError = createSelector([
    getPhotosStatus
], (photosStatus) => [photosStatus].includes('error'));

const selectIsPending = createSelector([
    (state: PhotoPrintsPdpState) => state.snapshot.pushStatus,
    (state: PhotoPrintsPdpState) => state.projectLoading.loadStatus,
    (state: PhotoPrintsPdpState) => state.auth.authStatus
], (pushStatus, loadStatus, authStatus) => {
    return [pushStatus, loadStatus, authStatus].includes('pending');
});

const selectDisabled = createSelector([
    selectHasError,
    selectIsPending,
    selectAllPhotos
], (hasError, isPending, photos) => {
    return hasError
        || isPending
        || photos.length === 0;
});